import React from 'react'
import { omConfirm } from '@owma/kit'
import MergeContact from './Merge'
import contactHelper from 'pages/contacts/components/SharedContacts/Helper'
import {
  createSaveData,
  isDataModified,
  validateEditData,
  createVCardData,
  mergeContactEditData,
  createEditDataFromDetailData,
} from './EditData'
import * as contactService from 'pages/contacts/modules/contactService'
import i18n from 'i18n'
import { NameObj, NameSplitter } from 'pages/contacts/utils/NameSplitter'
import DetailData from 'pages/contacts/components/DetailData'

const checkDuplicatedContacts = async (
  aEditData: any,
  onProcess: (isProcessing: boolean) => void,
  onComplete: (success: boolean) => void,
  onCancel: () => void,
) => {
  const emails = aEditData.emails
    .map((email: any) => email.value)
    .filter((email: string) => email.trim().length > 0)
  const phones = aEditData.phones
    .map((phone: any) => phone.value)
    .filter((phone: string) => phone.trim().length > 0)

  if (emails.length === 0 && phones.length === 0) {
    return false
  }

  let duplicatedContacts = []
  if (aEditData.isPersonal) {
    duplicatedContacts = await contactService.searchContactsEmailsPhones(emails, phones)
  } else {
    duplicatedContacts = await contactHelper.findDuplicatedContacts(emails, phones)
  }

  if (duplicatedContacts.length > 0) {
    //TODO: merge and update
    const modalObj = omConfirm.confirm({
      title: null,
      content: (
        <MergeContact
          newContact={aEditData}
          duplicates={duplicatedContacts}
          onCreateNew={() => {
            doSave(aEditData, null, true, onProcess, (success: boolean) => {
              modalObj.destroy()
              onComplete(success)
            })
          }}
          onCancel={() => {
            modalObj.destroy()
            onCancel()
            onComplete(false)
          }}
          onMerge={async (targetId: string, personalData: any) => {
            onProcess(true)
            if (aEditData.isPersonal) {
              const detailData = new DetailData(personalData, 'personal')
              if (detailData.hasPhoto) {
                const photo = await contactService.getPhoto(detailData.id)
                detailData.photoUrl = photo.photo
              }
              const editData = createEditDataFromDetailData(detailData)
              const mergedEditData = mergeContactEditData(editData, aEditData)
              doSavePersonal(mergedEditData)
                .then(() => {
                  modalObj.destroy()
                  onComplete(true)
                })
                .catch((error: any) => {
                  console.error('error - ', error)
                  alert(i18n.t('contact.error.saveFailed'))
                  onComplete(false)
                })
                .finally(() => {
                  onProcess(false)
                  modalObj.destroy()
                })
            } else {
              doMerge(aEditData, targetId).then(() => {
                onProcess(false)
                modalObj.destroy()
                onComplete(true)
              })
            }
          }}
          onReplace={(targetId: string, hasPhoto: boolean, personalData: any) => {
            onProcess(true)
            if (aEditData.isPersonal) {
              aEditData.id = personalData.serverId
              aEditData.etag = personalData.etag
              aEditData.uid = personalData.contact.uid
              doSavePersonal(aEditData)
                .then(() => {
                  modalObj.destroy()
                  onComplete(true)
                })
                .catch((error: any) => {
                  console.error('error - ', error)
                  alert(i18n.t('contact.error.saveFailed'))
                  onComplete(false)
                })
                .finally(() => {
                  onProcess(false)
                  modalObj.destroy()
                })
            } else {
              doReplace(aEditData, targetId, hasPhoto).then(() => {
                onProcess(false)
                modalObj.destroy()
                onComplete(true)
              })
            }
          }}
        />
      ),
    })
    return true
  }
  return false
}

const doMerge = async (editData: any, targetId: string) => {
  try {
    const saveData = createSaveData(editData)
    await contactHelper.mergeContact(targetId, saveData, editData.photo?.file || null)
  } catch (error) {
    console.error('error - ', error)
    alert(i18n.t('contact.error.saveFailed'))
  }
}

const doReplace = async (editData: any, targetId: string, hasPhoto: boolean) => {
  try {
    const saveData = createSaveData(editData)
    saveData.id = targetId
    await contactHelper.updateContact(
      saveData,
      editData.photo?.file || null,
      hasPhoto ? !!editData.photo?.file : false,
    )
  } catch (error) {
    console.error('error - ', error)
    alert(i18n.t('contact.error.saveFailed'))
  }
}

const doSavePersonal = async (editData: any) => {
  const saveData = createVCardData(editData)
  await contactService.saveContact(editData.id, editData.etag, saveData)
}

export const doSave = async (
  editData: any,
  orgEditData: any,
  passDuplicatedCheck: boolean = false,
  onProcess: (isProcessing: boolean) => void,
  onComplete: (success: boolean) => void,
  onCancel?: () => void,
) => {
  const hasChanges = isDataModified(orgEditData, editData)
  if (!hasChanges) {
    return
  }

  if (editData.isPersonal) {
    onProcess(true)
    if (editData.id == null && !passDuplicatedCheck) {
      const duplicated = await checkDuplicatedContacts(editData, onProcess, onComplete, onCancel!)
      if (duplicated) {
        return
      }
    }

    try {
      await doSavePersonal(editData)
      onComplete(true)
    } catch (error) {
      console.error('error - ', error)
      alert(i18n.t('contact.error.saveFailed'))
      onComplete(false)
    } finally {
      onProcess(false)
    }
    return
  }

  const { isValid, errors } = validateEditData(editData)
  if (!isValid) {
    if (errors.emails) {
      alert(i18n.t('contact.error.email'))
    }
    return
  }

  const isNew = !orgEditData?.id

  try {
    if (isNew && !passDuplicatedCheck) {
      const duplicated = await checkDuplicatedContacts(editData, onProcess, onComplete, onCancel!)
      if (duplicated) {
        return
      }
    }

    onProcess(true)

    let photoChanged = !!editData.photo?.file
    let photoDelete = orgEditData?.photo?.url && !editData.photo?.url
    const nameObj = new NameObj({
      prefix: editData.prefix,
      first_name: editData.firstName,
      last_name: editData.lastName,
      middle_name: editData.middleName,
      suffix: editData.suffix,
    })
    const nameSplitter = new NameSplitter(navigator.language || 'en-US')
    nameSplitter.guessFullNameStyle(nameObj)
    editData.displayName = nameSplitter.joinWithName(nameObj, true, true)

    const saveData = createSaveData(editData)

    if (!isNew) {
      saveData.favorite = orgEditData?.favorite ? true : false
      contactHelper
        .updateContact(saveData, photoChanged ? editData.photo.file : undefined, photoDelete)
        .then((res: any) => {
          console.log('res - ', res)
          onComplete(true)
          onProcess(false)
        })
    } else {
      contactHelper
        .createContact(saveData, photoChanged ? editData.photo.file : undefined)
        .then((res: any) => {
          console.log('res - ', res)
          onComplete(true)
          onProcess(false)
        })
    }
  } catch (error) {
    console.error('error - ', error)
    alert(i18n.t('contact.error.saveFailed'))
    onComplete(false)
    onProcess(false)
  }
}
